import dayjs from 'dayjs';
import type {
    PickupTrackingDriverPositionType,
    PickupTrackingDriverType,
    PickupTrackingKpiHistoryType,
    PickupTrackingKpiType,
    PickupTrackingStopType,
} from '../../../graphql/types/pickupDriverTracking/PickupDriverTrackingTypes';
import { DASHBOARD_PACIFIC_TZ } from '../../../utils/dashboardPacificTime';
import { toMotionPoint } from '../../central/delivery-monitor/deliveryMonitorMotion';
import type {
    TrackingAnomaly,
    TrackingDriver,
    TrackingKpiHistory,
    TrackingKpiSnapshot,
    TrackingStop,
    TrackingStopStatus,
    TrackingVehicleType,
} from './pickupTrackingTypes';

// 后端返回 → 页面模型的纯转换。Long 经 GraphQL 序列化后运行时可能是字符串，数值字段统一 Number()

// 车型只认后端三档编码（主库车长 14 / 26 / 53 尺），其余为 null、不按 van 兜底
const VEHICLE: Record<string, TrackingVehicleType> = { VAN: 'van', TRUCK_26: 't26', TRUCK_53: 't53' };
const ANOMALY: Record<string, TrackingAnomaly> = {
    IDLE: 'idle',
    LONG_STAY: 'stay',
    ABNORMAL_STOP: 'abnormalStop',
    DEVIATE: 'deviate',
    MULTI_ACCOUNT: 'multi',
};
const STOP_STATUS: Record<string, TrackingStopStatus> = { OK: 'ok', FAIL: 'fail', CURRENT: 'cur' };
// 站点按实际上门顺序：已操作（成功 / 失败混排）→ 当前站 → 待上门
const STOP_GROUP: Record<TrackingStopStatus, number> = { ok: 0, fail: 0, cur: 1, wait: 2 };

const num = (v: number | string | null | undefined): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

/** epoch ms → 美西 HH:mm；缺失为 null */
export const pacificHm = (ms: number | string | null | undefined): string | null => {
    const n = num(ms);
    return n === null ? null : dayjs(n).tz(DASHBOARD_PACIFIC_TZ).format('HH:mm');
};

export const toTrackingStop = (s: PickupTrackingStopType): TrackingStop => {
    const lat = num(s.latitude);
    const lng = num(s.longitude);
    const start = pacificHm(s.expectWindowStartMs);
    const end = pacificHm(s.expectWindowEndMs);
    return {
        siteId: s.siteId === null || s.siteId === undefined ? '' : String(s.siteId),
        siteName: s.siteName ?? '',
        address: s.address ?? '',
        status: STOP_STATUS[s.status] ?? 'wait',
        stayMin: num(s.stayMinutes),
        operatedAt: pacificHm(s.operatedAtMs),
        operatedAtMs: num(s.operatedAtMs),
        expectWindow: start && end ? `${start}–${end}` : null,
        // 只缺一个也当没有坐标，不上图
        lat: lat === null || lng === null ? null : lat,
        lng: lat === null || lng === null ? null : lng,
    };
};

/**
 * 后端按「任务 + 计划站序 stop_number」出数，司机实际不按计划走。已操作的按操作时间正序（无时间的排在已操作末尾），
 * 当前站、待上门保持后端顺序（sort 稳定）。弹窗列表、回放折线、站点编号、下一站都用这个顺序
 */
const sortByVisit = (stops: TrackingStop[]): TrackingStop[] =>
    [...stops].sort((a, b) => {
        const g = STOP_GROUP[a.status] - STOP_GROUP[b.status];
        if (g !== 0 || STOP_GROUP[a.status] !== 0) return g;
        return (a.operatedAtMs ?? Number.MAX_SAFE_INTEGER) - (b.operatedAtMs ?? Number.MAX_SAFE_INTEGER);
    });

/**
 * @param polled 本轮轮询返回的位置（最近 1 分钟），没上报为 undefined
 * @param lastKnown 当天最后已知位置（[mergeLastKnown] 累积），不传按 polled
 */
export const toTrackingDriver = (
    d: PickupTrackingDriverType,
    polled: PickupTrackingDriverPositionType | undefined,
    lastKnown: PickupTrackingDriverPositionType | undefined = polled,
): TrackingDriver => {
    const stops = sortByVisit((d.stops ?? []).map(toTrackingStop));
    const key = String(d.driverAccountId);
    const id = d.driverCode || key;
    const anomalies: TrackingAnomaly[] = [];
    (d.anomalies ?? []).forEach((code) => {
        const a = ANOMALY[code];
        if (a && !anomalies.includes(a)) anomalies.push(a);
    });
    const lat = num(lastKnown?.latitude);
    const lng = num(lastKnown?.longitude);
    const located = lastKnown !== undefined && lat !== null && lng !== null;
    const fresh = located && polled !== undefined;
    return {
        key,
        id,
        name: d.driverName || id,
        phone: d.driverPhone || null,
        vehicleType: VEHICLE[d.vehicleCategory ?? ''] ?? null,
        anomalies,
        total: stops.length,
        ok: stops.filter((s) => s.status === 'ok').length,
        fail: stops.filter((s) => s.status === 'fail').length,
        startTime: pacificHm(d.startTimeMs),
        handoverTime: pacificHm(d.handoverTimeMs),
        position: located ? { lng, lat } : null,
        lastPoint: located ? toMotionPoint(lastKnown) : null,
        fresh,
        track: fresh ? (polled?.track ?? []).map(toMotionPoint) : [],
        stops,
    };
};

/**
 * 当天每个司机的最后已知位置：在 prev 上合并首屏整天查询与本轮轮询，同一司机取位置时间最新的一条。
 * 轮询只查最近 1 分钟，本轮没上报的司机靠它留在地图上（变灰）
 */
export const mergeLastKnown = (
    prev: ReadonlyMap<string, PickupTrackingDriverPositionType>,
    ...batches: (PickupTrackingDriverPositionType[] | null)[]
): Map<string, PickupTrackingDriverPositionType> => {
    const next = new Map(prev);
    batches.forEach((batch) =>
        (batch ?? []).forEach((p) => {
            const key = String(p.driverAccountId);
            const cur = next.get(key);
            if (!cur || (num(p.locationTimestampMs) ?? -1) > (num(cur.locationTimestampMs) ?? -1)) next.set(key, p);
        }),
    );
    return next;
};

/** 当期 / 上一 slot：计数缺失按 0（slot 本身缺失为 null） */
const toKpiOrZero = (k: PickupTrackingKpiType | null | undefined): TrackingKpiSnapshot | null =>
    k
        ? {
              calcSlot: k.calcSlot ?? '',
              appointments: num(k.appointments) ?? 0,
              pickedUp: num(k.pickedUp) ?? 0,
              drivers: num(k.drivers) ?? 0,
              failed: num(k.failed) ?? 0,
          }
        : null;

/** 昨日对照：任一计数缺失整体为 null，较昨日显示 `-`，不拿 0 编造对比 */
const toKpiStrict = (k: PickupTrackingKpiType | null | undefined): TrackingKpiSnapshot | null => {
    if (!k) return null;
    const appointments = num(k.appointments);
    const pickedUp = num(k.pickedUp);
    const drivers = num(k.drivers);
    const failed = num(k.failed);
    if (appointments === null || pickedUp === null || drivers === null || failed === null) return null;
    return { calcSlot: k.calcSlot ?? '', appointments, pickedUp, drivers, failed };
};

export const toKpiHistory = (k: PickupTrackingKpiHistoryType | null | undefined): TrackingKpiHistory => ({
    current: toKpiOrZero(k?.current),
    previous: toKpiOrZero(k?.previous),
    yesterday: toKpiStrict(k?.yesterday),
});
